/**
 * The backend's exact strings. Never renamed, never prettified — a local alias guarantees a mismatch
 * on the next contract change, and the mismatch would be about a payment's state.
 *
 * `as const` tuples rather than `enum`, because `erasableSyntaxOnly` forbids `enum` and a tuple is
 * both iterable at runtime and exact as a type.
 */

export const DEPOSIT_STATUSES = [
  'initiated',
  'awaiting_payment',
  'processing',
  'pending_review',
  'completed',
  'rejected',
  'failed',
  'expired',
  'cancelled',
  'reversed',
] as const;

export type DepositStatus = (typeof DEPOSIT_STATUSES)[number];

/**
 * Terminal means forever: polling stops, timers clear, and no mutation control renders (invariant
 * I-4). `processing` and `pending_review` are deliberately absent — they look final to a player but
 * the server can still move them.
 */
export const TERMINAL_STATUSES = [
  'completed',
  'rejected',
  'failed',
  'expired',
  'cancelled',
  'reversed',
] as const;

export type TerminalStatus = (typeof TERMINAL_STATUSES)[number];

export function isKnownStatus(status: string): status is DepositStatus {
  return (DEPOSIT_STATUSES as readonly string[]).includes(status);
}

export function isTerminalStatus(status: string): boolean {
  return isKnownStatus(status) && (TERMINAL_STATUSES as readonly DepositStatus[]).includes(status);
}

/** `completed` is the only status that may render success language, color, or iconography. */
export function isSuccessStatus(status: DepositStatus): boolean {
  return status === 'completed';
}

export const DEPOSIT_MODES = ['api', 'wallet'] as const;
export type DepositMode = (typeof DEPOSIT_MODES)[number];

/** Wallet rails offered by the backend. API tiles list the ones they accept in `rails`. */
export const PAYMENT_METHOD_CODES = ['jazzcash', 'easypaisa'] as const;
export type PaymentMethodCode = (typeof PAYMENT_METHOD_CODES)[number];

export function isPaymentMethodCode(value: string): value is PaymentMethodCode {
  return (PAYMENT_METHOD_CODES as readonly string[]).includes(value);
}

/** The backend-owned destination category on `kind: "wallet"` catalogue tiles. */
export const WALLET_ACCOUNT_TYPES = ['wallet_number', 'mobile_number', 'bank_account'] as const;
export type WalletAccountType = (typeof WALLET_ACCOUNT_TYPES)[number];

export function isWalletAccountType(value: string): value is WalletAccountType {
  return (WALLET_ACCOUNT_TYPES as readonly string[]).includes(value);
}

/**
 * Fallback VPG `gateway` when an `api` tile omits `rails` or lists none we recognise.
 * A tile that does list rails must use the player's pick (or its only rail) — never this default
 * while a second rail is on the tile. See ADR-037.
 */
export const DEFAULT_API_PAYMENT_METHOD_CODE: PaymentMethodCode = 'jazzcash';
